import { BaseEntity, EntitySaveOptions, EntityDeleteOptions, CompositeKey, ValidationResult, ValidationErrorInfo, ValidationErrorType, Metadata, ProviderType, DatabaseProviderBase, RunView } from "@memberjunction/core";
import { RegisterClass } from "@memberjunction/global";
import { z } from "zod";

     
 
/**
 * zod schema definition for the entity Conversation Details__Skip
 */
export const SkipConversationDetail__SkipSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key for the conversation detail record.`),
    ConversationID: z.string().describe(`
        * * Field Name: ConversationID
        * * Display Name: Conversation ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Conversations__Skip (vwConversations__Skip.ID)
        * * Description: FK to Skip.Conversation (required).`),
    ExternalID: z.string().nullable().describe(`
        * * Field Name: ExternalID
        * * Display Name: External ID
        * * SQL Data Type: uniqueidentifier
        * * Description: External ID from the client system. Used to correlate with client conversation detail records.`),
    Role: z.union([z.literal('AI'), z.literal('User')]).describe(`
        * * Field Name: Role
        * * Display Name: Role
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * AI
    *   * User
        * * Description: Role of the message sender: User or AI.`),
    PersonID: z.string().nullable().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: Optional FK to Skip.Contact for the user who sent this message.`),
    ParentID: z.string().nullable().describe(`
        * * Field Name: ParentID
        * * Display Name: Parent ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Conversation Details__Skip (vwConversationDetails__Skip.ID)
        * * Description: Optional FK to parent Skip.ConversationDetail. Used when this AI message is replying to a specific user message.`),
    Message: z.string().nullable().describe(`
        * * Field Name: Message
        * * Display Name: Message
        * * SQL Data Type: nvarchar(MAX)
        * * Description: The message content.`),
    UserComponentVersionID: z.string().nullable().describe(`
        * * Field Name: UserComponentVersionID
        * * Display Name: User Component Version ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: User Component Versions (vwUserComponentVersions.ID)
        * * Description: Optional FK to Skip.UserComponentVersion. The component/artifact generated by Skip in this message.`),
    AIAgentRunID: z.string().nullable().describe(`
        * * Field Name: AIAgentRunID
        * * Display Name: AI Agent Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: AI Agent Runs (vwAIAgentRuns.ID)
        * * Description: Optional FK to __mj.AIAgentRun. The agent run associated with this AI message.`),
    AdditionalDetails: z.string().nullable().describe(`
        * * Field Name: AdditionalDetails
        * * Display Name: Additional Details
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Optional JSON field for storing additional metadata. Can include: error details, performance metrics (completionTime), user feedback (userRating, userFeedback), AI insights (reflectionInsights, summaryOfEarlierConversation), visibility flags (hiddenToUser), or any other extensible data.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Conversation: z.string().nullable().describe(`
        * * Field Name: Conversation
        * * Display Name: Conversation
        * * SQL Data Type: nvarchar(255)`),
    Person: z.string().nullable().describe(`
        * * Field Name: Person
        * * Display Name: Person
        * * SQL Data Type: nvarchar(201)`),
    AIAgentRun: z.string().nullable().describe(`
        * * Field Name: AIAgentRun
        * * Display Name: AI Agent Run
        * * SQL Data Type: nvarchar(255)`),
    RootParentID: z.string().nullable().describe(`
        * * Field Name: RootParentID
        * * Display Name: Root Parent ID
        * * SQL Data Type: uniqueidentifier`),
    ParentIDDepth: z.number().nullable().describe(`
        * * Field Name: ParentIDDepth
        * * Display Name: Parent ID Depth
        * * SQL Data Type: int`),
    ParentIDPath: z.string().nullable().describe(`
        * * Field Name: ParentIDPath
        * * Display Name: Parent ID Path
        * * SQL Data Type: nvarchar(MAX)`),
    ParentIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: ParentIDIsLeaf
        * * Display Name: Parent ID Is Leaf
        * * SQL Data Type: bit`),
    ParentIDChildCount: z.number().nullable().describe(`
        * * Field Name: ParentIDChildCount
        * * Display Name: Parent ID Child Count
        * * SQL Data Type: int`),
});

export type SkipConversationDetail__SkipEntityType = z.infer<typeof SkipConversationDetail__SkipSchema>;

/**
 * zod schema definition for the entity Conversations__Skip
 */
export const SkipConversation__SkipSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key for the conversation record.`),
    ExternalID: z.string().describe(`
        * * Field Name: ExternalID
        * * Display Name: External ID
        * * SQL Data Type: uniqueidentifier
        * * Description: External ID from the client system (required). Used to correlate with client conversation records.`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: Organization this conversation belongs to (required).`),
    PersonID: z.string().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: Contact who initiated the conversation (required).`),
    Name: z.string().nullable().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(255)
        * * Description: User-friendly name for the conversation.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
    Person: z.string().describe(`
        * * Field Name: Person
        * * Display Name: Person
        * * SQL Data Type: nvarchar(201)`),
});

export type SkipConversation__SkipEntityType = z.infer<typeof SkipConversation__SkipSchema>;

/**
 * zod schema definition for the entity Learning Cycle Notes
 */
export const SkipLearningCycleNoteSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()
        * * Description: Primary key for the learning cycle note record.`),
    ConversationID: z.string().describe(`
        * * Field Name: ConversationID
        * * Display Name: Conversation ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Conversations__Skip (vwConversations__Skip.ID)
        * * Description: FK to Skip.Conversation (required). The conversation this note was extracted from.`),
    AIAgentNoteID: z.string().describe(`
        * * Field Name: AIAgentNoteID
        * * Display Name: AI Agent Note ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: AI Agent Notes (vwAIAgentNotes.ID)
        * * Description: FK to __mj.AIAgentNote (required). The actual note/insight extracted by the learning cycle.`),
    AIAgentLearningCycleID: z.string().nullable().describe(`
        * * Field Name: AIAgentLearningCycleID
        * * Display Name: AI Agent Learning Cycle ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: AI Agent Learning Cycles (vwAIAgentLearningCycles.ID)
        * * Description: Optional FK to __mj.AIAgentLearningCycle. The learning cycle run that extracted this note.`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: FK to Skip.Organization (required). Denormalized from conversation for fast filtering by organization.`),
    PersonID: z.string().nullable().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: Optional FK to Skip.Contact. Denormalized for fast filtering when note is about a specific contact.`),
    ExtractionStrategy: z.string().nullable().describe(`
        * * Field Name: ExtractionStrategy
        * * Display Name: Extraction Strategy
        * * SQL Data Type: nvarchar(50)
        * * Description: The analysis strategy used to extract this note (e.g., Full, Incremental, RecentN). Documents how the conversation was processed.`),
    MessagesAnalyzed: z.number().nullable().describe(`
        * * Field Name: MessagesAnalyzed
        * * Display Name: Messages Analyzed
        * * SQL Data Type: int
        * * Description: Number of conversation messages that were analyzed to extract this insight.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Conversation: z.string().nullable().describe(`
        * * Field Name: Conversation
        * * Display Name: Conversation
        * * SQL Data Type: nvarchar(255)`),
    AIAgentNote: z.string().nullable().describe(`
        * * Field Name: AIAgentNote
        * * Display Name: AI Agent Note
        * * SQL Data Type: nvarchar(MAX)`),
    AIAgentLearningCycle: z.string().nullable().describe(`
        * * Field Name: AIAgentLearningCycle
        * * Display Name: AI Agent Learning Cycle
        * * SQL Data Type: nvarchar(255)`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
    Person: z.string().nullable().describe(`
        * * Field Name: Person
        * * Display Name: Person
        * * SQL Data Type: nvarchar(201)`),
});

export type SkipLearningCycleNoteEntityType = z.infer<typeof SkipLearningCycleNoteSchema>;

/**
 * zod schema definition for the entity Notes
 */
export const SkipNoteSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: Organization this note belongs to (required).`),
    PersonID: z.string().nullable().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: Optional contact this note is associated with.`),
    Phase: z.union([z.literal('Coder'), z.literal('Data Expert'), z.literal('Requirements Expert'), z.literal('Software Architect'), z.literal('Technical Project Manager')]).or(z.string()) .describe(`
        * * Field Name: Phase
        * * Display Name: Phase
        * * SQL Data Type: nvarchar(500)
    * * Value List Type: ListOrUserEntry
    * * Possible Values 
    *   * Coder
    *   * Data Expert
    *   * Requirements Expert
    *   * Software Architect
    *   * Technical Project Manager
        * * Description: Skip sub-agent phase(s) this note relates to. Supports comma-delimited values for multi-phase notes (e.g., "Data Expert,Coder"). Valid phases: Requirements Expert, Technical Project Manager, Data Expert, Software Architect, Coder.`),
    Source: z.union([z.literal('Learning Cycle'), z.literal('Manual')]).describe(`
        * * Field Name: Source
        * * Display Name: Source
        * * SQL Data Type: nvarchar(15)
        * * Default Value: Manual
    * * Value List Type: List
    * * Possible Values 
    *   * Learning Cycle
    *   * Manual
        * * Description: Source of the note - Manual or Learning Cycle.`),
    Status: z.union([z.literal('Approved'), z.literal('Pending'), z.literal('Rejected')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(10)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Approved
    *   * Pending
    *   * Rejected
        * * Description: Approval status - Pending, Approved, or Rejected.`),
    Note: z.string().describe(`
        * * Field Name: Note
        * * Display Name: Note
        * * SQL Data Type: nvarchar(MAX)
        * * Description: The actual note content.`),
    Priority: z.number().describe(`
        * * Field Name: Priority
        * * Display Name: Priority
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Execution priority within phase (higher values execute first).`),
    LatestStatusChangeDate: z.date().nullable().describe(`
        * * Field Name: LatestStatusChangeDate
        * * Display Name: Latest Status Change Date
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp when the status was last changed.`),
    LatestStatusChangeByID: z.string().nullable().describe(`
        * * Field Name: LatestStatusChangeByID
        * * Display Name: Latest Status Change By ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: Contact who last changed the status.`),
    LatestStatusChangeReason: z.string().nullable().describe(`
        * * Field Name: LatestStatusChangeReason
        * * Display Name: Latest Status Change Reason
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Reason for the latest status change.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
    Person: z.string().nullable().describe(`
        * * Field Name: Person
        * * Display Name: Person
        * * SQL Data Type: nvarchar(201)`),
    LatestStatusChangeBy: z.string().nullable().describe(`
        * * Field Name: LatestStatusChangeBy
        * * Display Name: Latest Status Change By
        * * SQL Data Type: nvarchar(201)`),
});

export type SkipNoteEntityType = z.infer<typeof SkipNoteSchema>;

/**
 * zod schema definition for the entity User Component Feedbacks
 */
export const SkipUserComponentFeedbackSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ComponentVersionID: z.string().describe(`
        * * Field Name: ComponentVersionID
        * * Display Name: Component Version ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: User Component Versions (vwUserComponentVersions.ID)`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)`),
    PersonID: z.string().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)`),
    Rating: z.number().describe(`
        * * Field Name: Rating
        * * Display Name: Rating
        * * SQL Data Type: int
        * * Description: Normalized rating 1-100 (100=best, 1=worst)`),
    FeedbackType: z.string().nullable().describe(`
        * * Field Name: FeedbackType
        * * Display Name: Feedback Type
        * * SQL Data Type: nvarchar(50)
        * * Description: Optional UI context: how rating was collected (ThumbsUp, Stars, Slider, etc.)`),
    Comments: z.string().nullable().describe(`
        * * Field Name: Comments
        * * Display Name: Comments
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Optional user comments with feedback`),
    ConversationID: z.string().nullable().describe(`
        * * Field Name: ConversationID
        * * Display Name: Conversation ID
        * * SQL Data Type: nvarchar(500)
        * * Description: MJ conversation ID where feedback originated`),
    ConversationDetailID: z.string().nullable().describe(`
        * * Field Name: ConversationDetailID
        * * Display Name: Conversation Detail ID
        * * SQL Data Type: nvarchar(500)
        * * Description: MJ conversation detail/message ID`),
    ReportID: z.string().nullable().describe(`
        * * Field Name: ReportID
        * * Display Name: Report ID
        * * SQL Data Type: nvarchar(500)
        * * Description: MJ report ID if component was saved as report`),
    DashboardID: z.string().nullable().describe(`
        * * Field Name: DashboardID
        * * Display Name: Dashboard ID
        * * SQL Data Type: nvarchar(500)
        * * Description: MJ dashboard ID if component was saved as dashboard`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
    Person: z.string().describe(`
        * * Field Name: Person
        * * Display Name: Person
        * * SQL Data Type: nvarchar(201)`),
});

export type SkipUserComponentFeedbackEntityType = z.infer<typeof SkipUserComponentFeedbackSchema>;

/**
 * zod schema definition for the entity User Component Libraries
 */
export const SkipUserComponentLibrarySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ComponentVersionID: z.string().describe(`
        * * Field Name: ComponentVersionID
        * * Display Name: Component Version ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: User Component Versions (vwUserComponentVersions.ID)`),
    LibraryName: z.string().describe(`
        * * Field Name: LibraryName
        * * Display Name: Library Name
        * * SQL Data Type: nvarchar(500)`),
    Version: z.string().nullable().describe(`
        * * Field Name: Version
        * * Display Name: Version
        * * SQL Data Type: nvarchar(100)`),
    GlobalVariable: z.string().nullable().describe(`
        * * Field Name: GlobalVariable
        * * Display Name: Global Variable
        * * SQL Data Type: nvarchar(255)`),
    CDNUrl: z.string().nullable().describe(`
        * * Field Name: CDNUrl
        * * Display Name: CDN Url
        * * SQL Data Type: nvarchar(1000)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type SkipUserComponentLibraryEntityType = z.infer<typeof SkipUserComponentLibrarySchema>;

/**
 * zod schema definition for the entity User Component Usages
 */
export const SkipUserComponentUsageSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ComponentVersionID: z.string().describe(`
        * * Field Name: ComponentVersionID
        * * Display Name: Component Version ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: User Component Versions (vwUserComponentVersions.ID)`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)`),
    PersonID: z.string().nullable().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)`),
    APIKeyID: z.string().nullable().describe(`
        * * Field Name: APIKeyID
        * * Display Name: API Key ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: API Keys (vwAPIKeys.ID)
        * * Description: Which API key was used for this access`),
    AccessedAt: z.date().describe(`
        * * Field Name: AccessedAt
        * * Display Name: Accessed At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: When the component was accessed`),
    AccessType: z.union([z.literal('CacheValidation'), z.literal('Download'), z.literal('Execution')]).describe(`
        * * Field Name: AccessType
        * * Display Name: Access Type
        * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * CacheValidation
    *   * Download
    *   * Execution
        * * Description: Type of access: Download, CacheValidation, or Execution`),
    ClientHash: z.string().nullable().describe(`
        * * Field Name: ClientHash
        * * Display Name: Client Hash
        * * SQL Data Type: nvarchar(64)
        * * Description: Component hash provided by client for cache validation`),
    CacheHit: z.boolean().describe(`
        * * Field Name: CacheHit
        * * Display Name: Cache Hit
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: True if client cache was valid (204 response)`),
    ResponseCode: z.number().describe(`
        * * Field Name: ResponseCode
        * * Display Name: Response Code
        * * SQL Data Type: int
        * * Description: HTTP response code returned to client`),
    ExecutionContext: z.string().nullable().describe(`
        * * Field Name: ExecutionContext
        * * Display Name: Execution Context
        * * SQL Data Type: nvarchar(500)
        * * Description: Context where component is being executed`),
    ParentComponentID: z.string().nullable().describe(`
        * * Field Name: ParentComponentID
        * * Display Name: Parent Component ID
        * * SQL Data Type: uniqueidentifier
        * * Description: Parent component if this is nested`),
    SessionID: z.string().nullable().describe(`
        * * Field Name: SessionID
        * * Display Name: Session ID
        * * SQL Data Type: nvarchar(255)
        * * Description: Session identifier for usage pattern analysis`),
    UserAgent: z.string().nullable().describe(`
        * * Field Name: UserAgent
        * * Display Name: User Agent
        * * SQL Data Type: nvarchar(1000)
        * * Description: Client user agent string`),
    IPAddress: z.string().nullable().describe(`
        * * Field Name: IPAddress
        * * Display Name: IP Address
        * * SQL Data Type: nvarchar(45)
        * * Description: Client IP address for geographic analytics`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
    Person: z.string().nullable().describe(`
        * * Field Name: Person
        * * Display Name: Person
        * * SQL Data Type: nvarchar(201)`),
    APIKey: z.string().nullable().describe(`
        * * Field Name: APIKey
        * * Display Name: API Key
        * * SQL Data Type: nvarchar(255)`),
});

export type SkipUserComponentUsageEntityType = z.infer<typeof SkipUserComponentUsageSchema>;

/**
 * zod schema definition for the entity User Component Versions
 */
export const SkipUserComponentVersionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ComponentID: z.string().describe(`
        * * Field Name: ComponentID
        * * Display Name: Component ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: User Components (vwUserComponents.ID)`),
    Version: z.string().describe(`
        * * Field Name: Version
        * * Display Name: Version
        * * SQL Data Type: nvarchar(50)
        * * Description: Semantic version number (e.g., 1.0.0)`),
    Specification: z.string().describe(`
        * * Field Name: Specification
        * * Display Name: Specification
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Full component JSON specification including code and metadata`),
    SpecificationHash: z.string().describe(`
        * * Field Name: SpecificationHash
        * * Display Name: Specification Hash
        * * SQL Data Type: nvarchar(64)
        * * Description: SHA-256 hash of specification for cache validation`),
    UserRequest: z.string().nullable().describe(`
        * * Field Name: UserRequest
        * * Display Name: User Request
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Original user prompt or request that triggered generation`),
    GenerationContext: z.string().nullable().describe(`
        * * Field Name: GenerationContext
        * * Display Name: Generation Context
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON context including model used, parameters, and generation settings`),
    GenerationType: z.union([z.literal('Initial'), z.literal('Revision')]).describe(`
        * * Field Name: GenerationType
        * * Display Name: Generation Type
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Initial
    * * Value List Type: List
    * * Possible Values 
    *   * Initial
    *   * Revision
        * * Description: Type of generation: Initial or Revision`),
    RevisionRequest: z.string().nullable().describe(`
        * * Field Name: RevisionRequest
        * * Display Name: Revision Request
        * * SQL Data Type: nvarchar(MAX)
        * * Description: User revision request if this is a revision`),
    RevisionSentiment: z.string().nullable().describe(`
        * * Field Name: RevisionSentiment
        * * Display Name: Revision Sentiment
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON sentiment analysis of revision request`),
    UserSentimentScore: z.number().nullable().describe(`
        * * Field Name: UserSentimentScore
        * * Display Name: User Sentiment Score
        * * SQL Data Type: int
        * * Description: Inferred user sentiment score from 0-100 based on feedback language`),
    HasCustomProps: z.boolean().nullable().describe(`
        * * Field Name: HasCustomProps
        * * Display Name: Has Custom Props
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether component accepts custom props beyond standard ones`),
    HasRequiredCustomProps: z.boolean().nullable().describe(`
        * * Field Name: HasRequiredCustomProps
        * * Display Name: Has Required Custom Props
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether component has required custom props`),
    HasCustomEvents: z.boolean().nullable().describe(`
        * * Field Name: HasCustomEvents
        * * Display Name: Has Custom Events
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether component emits custom events`),
    RequiresData: z.boolean().nullable().describe(`
        * * Field Name: RequiresData
        * * Display Name: Requires Data
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether component requires data from parent or loads its own`),
    DependencyCount: z.number().nullable().describe(`
        * * Field Name: DependencyCount
        * * Display Name: Dependency Count
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Number of child components this component depends on`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Component: z.string().describe(`
        * * Field Name: Component
        * * Display Name: Component
        * * SQL Data Type: nvarchar(250)`),
    ComponentNamespace: z.string().describe(`
        * * Field Name: ComponentNamespace
        * * Display Name: Component Namespace
        * * SQL Data Type: nvarchar(599)`),
    ComponentOrganizationID: z.string().describe(`
        * * Field Name: ComponentOrganizationID
        * * Display Name: Component Organization ID
        * * SQL Data Type: uniqueidentifier`),
});

export type SkipUserComponentVersionEntityType = z.infer<typeof SkipUserComponentVersionSchema>;

/**
 * zod schema definition for the entity User Components
 */
export const SkipUserComponentSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)`),
    PersonID: z.string().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)`),
    Namespace: z.string().describe(`
        * * Field Name: Namespace
        * * Display Name: Namespace
        * * SQL Data Type: nvarchar(599)
        * * Description: Hierarchical namespace path following format @skip/org-{orgId}/contact-{contactId}`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(250)
        * * Description: Component name within the namespace, must be unique within namespace`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(1000)
        * * Description: User-friendly display title for UI presentation`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Detailed description of component functionality and purpose`),
    Type: z.string().nullable().describe(`
        * * Field Name: Type
        * * Display Name: Type
        * * SQL Data Type: nvarchar(255)
        * * Description: Component type classification: Report, Dashboard, Form, Table, Chart, Navigation, Search, Widget, Utility, or Other`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
    Person: z.string().describe(`
        * * Field Name: Person
        * * Display Name: Person
        * * SQL Data Type: nvarchar(201)`),
});

export type SkipUserComponentEntityType = z.infer<typeof SkipUserComponentSchema>;
 
 

/**
 * Conversation Details__Skip - strongly typed entity sub-class
 * * Schema: Skip
 * * Base Table: ConversationDetail
 * * Base View: vwConversationDetails__Skip
 * * @description Stores individual messages within Skip conversations. Each record represents a single turn in the conversation (user message or AI response).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Conversation Details__Skip')
export class SkipConversationDetail__SkipEntity extends BaseEntity<SkipConversationDetail__SkipEntityType> {
    /**
    * Loads the Conversation Details__Skip record from the database
    * @param ID: string - primary key value to load the Conversation Details__Skip record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof SkipConversationDetail__SkipEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key for the conversation detail record.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ConversationID
    * * Display Name: Conversation ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Conversations__Skip (vwConversations__Skip.ID)
    * * Description: FK to Skip.Conversation (required).
    */
    get ConversationID(): string {
        return this.Get('ConversationID');
    }
    set ConversationID(value: string) {
        this.Set('ConversationID', value);
    }

    /**
    * * Field Name: ExternalID
    * * Display Name: External ID
    * * SQL Data Type: uniqueidentifier
    * * Description: External ID from the client system. Used to correlate with client conversation detail records.
    */
    get ExternalID(): string | null {
        return this.Get('ExternalID');
    }
    set ExternalID(value: string | null) {
        this.Set('ExternalID', value);
    }

    /**
    * * Field Name: Role
    * * Display Name: Role
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * AI
    *   * User
    * * Description: Role of the message sender: User or AI.
    */
    get Role(): 'AI' | 'User' {
        return this.Get('Role');
    }
    set Role(value: 'AI' | 'User') {
        this.Set('Role', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: Optional FK to Skip.Contact for the user who sent this message.
    */
    get PersonID(): string | null {
        return this.Get('PersonID');
    }
    set PersonID(value: string | null) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: ParentID
    * * Display Name: Parent ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Conversation Details__Skip (vwConversationDetails__Skip.ID)
    * * Description: Optional FK to parent Skip.ConversationDetail. Used when this AI message is replying to a specific user message.
    */
    get ParentID(): string | null {
        return this.Get('ParentID');
    }
    set ParentID(value: string | null) {
        this.Set('ParentID', value);
    }

    /**
    * * Field Name: Message
    * * Display Name: Message
    * * SQL Data Type: nvarchar(MAX)
    * * Description: The message content.
    */
    get Message(): string | null {
        return this.Get('Message');
    }
    set Message(value: string | null) {
        this.Set('Message', value);
    }

    /**
    * * Field Name: UserComponentVersionID
    * * Display Name: User Component Version ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: User Component Versions (vwUserComponentVersions.ID)
    * * Description: Optional FK to Skip.UserComponentVersion. The component/artifact generated by Skip in this message.
    */
    get UserComponentVersionID(): string | null {
        return this.Get('UserComponentVersionID');
    }
    set UserComponentVersionID(value: string | null) {
        this.Set('UserComponentVersionID', value);
    }

    /**
    * * Field Name: AIAgentRunID
    * * Display Name: AI Agent Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: AI Agent Runs (vwAIAgentRuns.ID)
    * * Description: Optional FK to __mj.AIAgentRun. The agent run associated with this AI message.
    */
    get AIAgentRunID(): string | null {
        return this.Get('AIAgentRunID');
    }
    set AIAgentRunID(value: string | null) {
        this.Set('AIAgentRunID', value);
    }

    /**
    * * Field Name: AdditionalDetails
    * * Display Name: Additional Details
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Optional JSON field for storing additional metadata. Can include: error details, performance metrics (completionTime), user feedback (userRating, userFeedback), AI insights (reflectionInsights, summaryOfEarlierConversation), visibility flags (hiddenToUser), or any other extensible data.
    */
    get AdditionalDetails(): string | null {
        return this.Get('AdditionalDetails');
    }
    set AdditionalDetails(value: string | null) {
        this.Set('AdditionalDetails', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Conversation
    * * Display Name: Conversation
    * * SQL Data Type: nvarchar(255)
    */
    get Conversation(): string | null {
        return this.Get('Conversation');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person
    * * SQL Data Type: nvarchar(201)
    */
    get Person(): string | null {
        return this.Get('Person');
    }

    /**
    * * Field Name: AIAgentRun
    * * Display Name: AI Agent Run
    * * SQL Data Type: nvarchar(255)
    */
    get AIAgentRun(): string | null {
        return this.Get('AIAgentRun');
    }

    /**
    * * Field Name: RootParentID
    * * Display Name: Root Parent ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootParentID(): string | null {
        return this.Get('RootParentID');
    }

    /**
    * * Field Name: ParentIDDepth
    * * Display Name: Parent ID Depth
    * * SQL Data Type: int
    */
    get ParentIDDepth(): number | null {
        return this.Get('ParentIDDepth');
    }

    /**
    * * Field Name: ParentIDPath
    * * Display Name: Parent ID Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get ParentIDPath(): string | null {
        return this.Get('ParentIDPath');
    }

    /**
    * * Field Name: ParentIDIsLeaf
    * * Display Name: Parent ID Is Leaf
    * * SQL Data Type: bit
    */
    get ParentIDIsLeaf(): boolean | null {
        return this.Get('ParentIDIsLeaf');
    }

    /**
    * * Field Name: ParentIDChildCount
    * * Display Name: Parent ID Child Count
    * * SQL Data Type: int
    */
    get ParentIDChildCount(): number | null {
        return this.Get('ParentIDChildCount');
    }
}


/**
 * Conversations__Skip - strongly typed entity sub-class
 * * Schema: Skip
 * * Base Table: Conversation
 * * Base View: vwConversations__Skip
 * * @description Stores conversations between external clients and the Skip AI system. This is separate from __mj.Conversation which handles internal MemberJunction conversations.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Conversations__Skip')
export class SkipConversation__SkipEntity extends BaseEntity<SkipConversation__SkipEntityType> {
    /**
    * Loads the Conversations__Skip record from the database
    * @param ID: string - primary key value to load the Conversations__Skip record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof SkipConversation__SkipEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key for the conversation record.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ExternalID
    * * Display Name: External ID
    * * SQL Data Type: uniqueidentifier
    * * Description: External ID from the client system (required). Used to correlate with client conversation records.
    */
    get ExternalID(): string {
        return this.Get('ExternalID');
    }
    set ExternalID(value: string) {
        this.Set('ExternalID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: Organization this conversation belongs to (required).
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: Contact who initiated the conversation (required).
    */
    get PersonID(): string {
        return this.Get('PersonID');
    }
    set PersonID(value: string) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(255)
    * * Description: User-friendly name for the conversation.
    */
    get Name(): string | null {
        return this.Get('Name');
    }
    set Name(value: string | null) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person
    * * SQL Data Type: nvarchar(201)
    */
    get Person(): string {
        return this.Get('Person');
    }
}


/**
 * Learning Cycle Notes - strongly typed entity sub-class
 * * Schema: Skip
 * * Base Table: LearningCycleNote
 * * Base View: vwLearningCycleNotes
 * * @description Junction table linking Skip conversations to learning cycle notes in __mj.AIAgentNote. Enables traceability from insights back to source conversations.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Learning Cycle Notes')
export class SkipLearningCycleNoteEntity extends BaseEntity<SkipLearningCycleNoteEntityType> {
    /**
    * Loads the Learning Cycle Notes record from the database
    * @param ID: string - primary key value to load the Learning Cycle Notes record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof SkipLearningCycleNoteEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    * * Description: Primary key for the learning cycle note record.
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ConversationID
    * * Display Name: Conversation ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Conversations__Skip (vwConversations__Skip.ID)
    * * Description: FK to Skip.Conversation (required). The conversation this note was extracted from.
    */
    get ConversationID(): string {
        return this.Get('ConversationID');
    }
    set ConversationID(value: string) {
        this.Set('ConversationID', value);
    }

    /**
    * * Field Name: AIAgentNoteID
    * * Display Name: AI Agent Note ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: AI Agent Notes (vwAIAgentNotes.ID)
    * * Description: FK to __mj.AIAgentNote (required). The actual note/insight extracted by the learning cycle.
    */
    get AIAgentNoteID(): string {
        return this.Get('AIAgentNoteID');
    }
    set AIAgentNoteID(value: string) {
        this.Set('AIAgentNoteID', value);
    }

    /**
    * * Field Name: AIAgentLearningCycleID
    * * Display Name: AI Agent Learning Cycle ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: AI Agent Learning Cycles (vwAIAgentLearningCycles.ID)
    * * Description: Optional FK to __mj.AIAgentLearningCycle. The learning cycle run that extracted this note.
    */
    get AIAgentLearningCycleID(): string | null {
        return this.Get('AIAgentLearningCycleID');
    }
    set AIAgentLearningCycleID(value: string | null) {
        this.Set('AIAgentLearningCycleID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: FK to Skip.Organization (required). Denormalized from conversation for fast filtering by organization.
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: Optional FK to Skip.Contact. Denormalized for fast filtering when note is about a specific contact.
    */
    get PersonID(): string | null {
        return this.Get('PersonID');
    }
    set PersonID(value: string | null) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: ExtractionStrategy
    * * Display Name: Extraction Strategy
    * * SQL Data Type: nvarchar(50)
    * * Description: The analysis strategy used to extract this note (e.g., Full, Incremental, RecentN). Documents how the conversation was processed.
    */
    get ExtractionStrategy(): string | null {
        return this.Get('ExtractionStrategy');
    }
    set ExtractionStrategy(value: string | null) {
        this.Set('ExtractionStrategy', value);
    }

    /**
    * * Field Name: MessagesAnalyzed
    * * Display Name: Messages Analyzed
    * * SQL Data Type: int
    * * Description: Number of conversation messages that were analyzed to extract this insight.
    */
    get MessagesAnalyzed(): number | null {
        return this.Get('MessagesAnalyzed');
    }
    set MessagesAnalyzed(value: number | null) {
        this.Set('MessagesAnalyzed', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Conversation
    * * Display Name: Conversation
    * * SQL Data Type: nvarchar(255)
    */
    get Conversation(): string | null {
        return this.Get('Conversation');
    }

    /**
    * * Field Name: AIAgentNote
    * * Display Name: AI Agent Note
    * * SQL Data Type: nvarchar(MAX)
    */
    get AIAgentNote(): string | null {
        return this.Get('AIAgentNote');
    }

    /**
    * * Field Name: AIAgentLearningCycle
    * * Display Name: AI Agent Learning Cycle
    * * SQL Data Type: nvarchar(255)
    */
    get AIAgentLearningCycle(): string | null {
        return this.Get('AIAgentLearningCycle');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person
    * * SQL Data Type: nvarchar(201)
    */
    get Person(): string | null {
        return this.Get('Person');
    }
}


/**
 * Notes - strongly typed entity sub-class
 * * Schema: Skip
 * * Base Table: Note
 * * Base View: vwNotes
 * * @description Stores notes associated with organizations and contacts throughout the Skip learning and generation process.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Notes')
export class SkipNoteEntity extends BaseEntity<SkipNoteEntityType> {
    /**
    * Loads the Notes record from the database
    * @param ID: string - primary key value to load the Notes record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof SkipNoteEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: Organization this note belongs to (required).
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: Optional contact this note is associated with.
    */
    get PersonID(): string | null {
        return this.Get('PersonID');
    }
    set PersonID(value: string | null) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: Phase
    * * Display Name: Phase
    * * SQL Data Type: nvarchar(500)
    * * Value List Type: ListOrUserEntry
    * * Possible Values 
    *   * Coder
    *   * Data Expert
    *   * Requirements Expert
    *   * Software Architect
    *   * Technical Project Manager
    * * Description: Skip sub-agent phase(s) this note relates to. Supports comma-delimited values for multi-phase notes (e.g., "Data Expert,Coder"). Valid phases: Requirements Expert, Technical Project Manager, Data Expert, Software Architect, Coder.
    */
    get Phase(): 'Coder' | 'Data Expert' | 'Requirements Expert' | 'Software Architect' | 'Technical Project Manager' | string {
        return this.Get('Phase');
    }
    set Phase(value: 'Coder' | 'Data Expert' | 'Requirements Expert' | 'Software Architect' | 'Technical Project Manager' | string) {
        this.Set('Phase', value);
    }

    /**
    * * Field Name: Source
    * * Display Name: Source
    * * SQL Data Type: nvarchar(15)
    * * Default Value: Manual
    * * Value List Type: List
    * * Possible Values 
    *   * Learning Cycle
    *   * Manual
    * * Description: Source of the note - Manual or Learning Cycle.
    */
    get Source(): 'Learning Cycle' | 'Manual' {
        return this.Get('Source');
    }
    set Source(value: 'Learning Cycle' | 'Manual') {
        this.Set('Source', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(10)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Approved
    *   * Pending
    *   * Rejected
    * * Description: Approval status - Pending, Approved, or Rejected.
    */
    get Status(): 'Approved' | 'Pending' | 'Rejected' {
        return this.Get('Status');
    }
    set Status(value: 'Approved' | 'Pending' | 'Rejected') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: Note
    * * Display Name: Note
    * * SQL Data Type: nvarchar(MAX)
    * * Description: The actual note content.
    */
    get Note(): string {
        return this.Get('Note');
    }
    set Note(value: string) {
        this.Set('Note', value);
    }

    /**
    * * Field Name: Priority
    * * Display Name: Priority
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Execution priority within phase (higher values execute first).
    */
    get Priority(): number {
        return this.Get('Priority');
    }
    set Priority(value: number) {
        this.Set('Priority', value);
    }

    /**
    * * Field Name: LatestStatusChangeDate
    * * Display Name: Latest Status Change Date
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp when the status was last changed.
    */
    get LatestStatusChangeDate(): Date | null {
        return this.Get('LatestStatusChangeDate');
    }
    set LatestStatusChangeDate(value: Date | null) {
        this.Set('LatestStatusChangeDate', value);
    }

    /**
    * * Field Name: LatestStatusChangeByID
    * * Display Name: Latest Status Change By ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: Contact who last changed the status.
    */
    get LatestStatusChangeByID(): string | null {
        return this.Get('LatestStatusChangeByID');
    }
    set LatestStatusChangeByID(value: string | null) {
        this.Set('LatestStatusChangeByID', value);
    }

    /**
    * * Field Name: LatestStatusChangeReason
    * * Display Name: Latest Status Change Reason
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Reason for the latest status change.
    */
    get LatestStatusChangeReason(): string | null {
        return this.Get('LatestStatusChangeReason');
    }
    set LatestStatusChangeReason(value: string | null) {
        this.Set('LatestStatusChangeReason', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person
    * * SQL Data Type: nvarchar(201)
    */
    get Person(): string | null {
        return this.Get('Person');
    }

    /**
    * * Field Name: LatestStatusChangeBy
    * * Display Name: Latest Status Change By
    * * SQL Data Type: nvarchar(201)
    */
    get LatestStatusChangeBy(): string | null {
        return this.Get('LatestStatusChangeBy');
    }
}


/**
 * User Component Feedbacks - strongly typed entity sub-class
 * * Schema: Skip
 * * Base Table: UserComponentFeedback
 * * Base View: vwUserComponentFeedbacks
 * * @description Stores user feedback with normalized ratings
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'User Component Feedbacks')
export class SkipUserComponentFeedbackEntity extends BaseEntity<SkipUserComponentFeedbackEntityType> {
    /**
    * Loads the User Component Feedbacks record from the database
    * @param ID: string - primary key value to load the User Component Feedbacks record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof SkipUserComponentFeedbackEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ComponentVersionID
    * * Display Name: Component Version ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: User Component Versions (vwUserComponentVersions.ID)
    */
    get ComponentVersionID(): string {
        return this.Get('ComponentVersionID');
    }
    set ComponentVersionID(value: string) {
        this.Set('ComponentVersionID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    */
    get PersonID(): string {
        return this.Get('PersonID');
    }
    set PersonID(value: string) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: Rating
    * * Display Name: Rating
    * * SQL Data Type: int
    * * Description: Normalized rating 1-100 (100=best, 1=worst)
    */
    get Rating(): number {
        return this.Get('Rating');
    }
    set Rating(value: number) {
        this.Set('Rating', value);
    }

    /**
    * * Field Name: FeedbackType
    * * Display Name: Feedback Type
    * * SQL Data Type: nvarchar(50)
    * * Description: Optional UI context: how rating was collected (ThumbsUp, Stars, Slider, etc.)
    */
    get FeedbackType(): string | null {
        return this.Get('FeedbackType');
    }
    set FeedbackType(value: string | null) {
        this.Set('FeedbackType', value);
    }

    /**
    * * Field Name: Comments
    * * Display Name: Comments
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Optional user comments with feedback
    */
    get Comments(): string | null {
        return this.Get('Comments');
    }
    set Comments(value: string | null) {
        this.Set('Comments', value);
    }

    /**
    * * Field Name: ConversationID
    * * Display Name: Conversation ID
    * * SQL Data Type: nvarchar(500)
    * * Description: MJ conversation ID where feedback originated
    */
    get ConversationID(): string | null {
        return this.Get('ConversationID');
    }
    set ConversationID(value: string | null) {
        this.Set('ConversationID', value);
    }

    /**
    * * Field Name: ConversationDetailID
    * * Display Name: Conversation Detail ID
    * * SQL Data Type: nvarchar(500)
    * * Description: MJ conversation detail/message ID
    */
    get ConversationDetailID(): string | null {
        return this.Get('ConversationDetailID');
    }
    set ConversationDetailID(value: string | null) {
        this.Set('ConversationDetailID', value);
    }

    /**
    * * Field Name: ReportID
    * * Display Name: Report ID
    * * SQL Data Type: nvarchar(500)
    * * Description: MJ report ID if component was saved as report
    */
    get ReportID(): string | null {
        return this.Get('ReportID');
    }
    set ReportID(value: string | null) {
        this.Set('ReportID', value);
    }

    /**
    * * Field Name: DashboardID
    * * Display Name: Dashboard ID
    * * SQL Data Type: nvarchar(500)
    * * Description: MJ dashboard ID if component was saved as dashboard
    */
    get DashboardID(): string | null {
        return this.Get('DashboardID');
    }
    set DashboardID(value: string | null) {
        this.Set('DashboardID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person
    * * SQL Data Type: nvarchar(201)
    */
    get Person(): string {
        return this.Get('Person');
    }
}


/**
 * User Component Libraries - strongly typed entity sub-class
 * * Schema: Skip
 * * Base Table: UserComponentLibrary
 * * Base View: vwUserComponentLibraries
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'User Component Libraries')
export class SkipUserComponentLibraryEntity extends BaseEntity<SkipUserComponentLibraryEntityType> {
    /**
    * Loads the User Component Libraries record from the database
    * @param ID: string - primary key value to load the User Component Libraries record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof SkipUserComponentLibraryEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ComponentVersionID
    * * Display Name: Component Version ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: User Component Versions (vwUserComponentVersions.ID)
    */
    get ComponentVersionID(): string {
        return this.Get('ComponentVersionID');
    }
    set ComponentVersionID(value: string) {
        this.Set('ComponentVersionID', value);
    }

    /**
    * * Field Name: LibraryName
    * * Display Name: Library Name
    * * SQL Data Type: nvarchar(500)
    */
    get LibraryName(): string {
        return this.Get('LibraryName');
    }
    set LibraryName(value: string) {
        this.Set('LibraryName', value);
    }

    /**
    * * Field Name: Version
    * * Display Name: Version
    * * SQL Data Type: nvarchar(100)
    */
    get Version(): string | null {
        return this.Get('Version');
    }
    set Version(value: string | null) {
        this.Set('Version', value);
    }

    /**
    * * Field Name: GlobalVariable
    * * Display Name: Global Variable
    * * SQL Data Type: nvarchar(255)
    */
    get GlobalVariable(): string | null {
        return this.Get('GlobalVariable');
    }
    set GlobalVariable(value: string | null) {
        this.Set('GlobalVariable', value);
    }

    /**
    * * Field Name: CDNUrl
    * * Display Name: CDN Url
    * * SQL Data Type: nvarchar(1000)
    */
    get CDNUrl(): string | null {
        return this.Get('CDNUrl');
    }
    set CDNUrl(value: string | null) {
        this.Set('CDNUrl', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * User Component Usages - strongly typed entity sub-class
 * * Schema: Skip
 * * Base Table: UserComponentUsage
 * * Base View: vwUserComponentUsages
 * * @description Tracks every component access for usage analytics and billing
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'User Component Usages')
export class SkipUserComponentUsageEntity extends BaseEntity<SkipUserComponentUsageEntityType> {
    /**
    * Loads the User Component Usages record from the database
    * @param ID: string - primary key value to load the User Component Usages record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof SkipUserComponentUsageEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ComponentVersionID
    * * Display Name: Component Version ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: User Component Versions (vwUserComponentVersions.ID)
    */
    get ComponentVersionID(): string {
        return this.Get('ComponentVersionID');
    }
    set ComponentVersionID(value: string) {
        this.Set('ComponentVersionID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    */
    get PersonID(): string | null {
        return this.Get('PersonID');
    }
    set PersonID(value: string | null) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: APIKeyID
    * * Display Name: API Key ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: API Keys (vwAPIKeys.ID)
    * * Description: Which API key was used for this access
    */
    get APIKeyID(): string | null {
        return this.Get('APIKeyID');
    }
    set APIKeyID(value: string | null) {
        this.Set('APIKeyID', value);
    }

    /**
    * * Field Name: AccessedAt
    * * Display Name: Accessed At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: When the component was accessed
    */
    get AccessedAt(): Date {
        return this.Get('AccessedAt');
    }
    set AccessedAt(value: Date) {
        this.Set('AccessedAt', value);
    }

    /**
    * * Field Name: AccessType
    * * Display Name: Access Type
    * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * CacheValidation
    *   * Download
    *   * Execution
    * * Description: Type of access: Download, CacheValidation, or Execution
    */
    get AccessType(): 'CacheValidation' | 'Download' | 'Execution' {
        return this.Get('AccessType');
    }
    set AccessType(value: 'CacheValidation' | 'Download' | 'Execution') {
        this.Set('AccessType', value);
    }

    /**
    * * Field Name: ClientHash
    * * Display Name: Client Hash
    * * SQL Data Type: nvarchar(64)
    * * Description: Component hash provided by client for cache validation
    */
    get ClientHash(): string | null {
        return this.Get('ClientHash');
    }
    set ClientHash(value: string | null) {
        this.Set('ClientHash', value);
    }

    /**
    * * Field Name: CacheHit
    * * Display Name: Cache Hit
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: True if client cache was valid (204 response)
    */
    get CacheHit(): boolean {
        return this.Get('CacheHit');
    }
    set CacheHit(value: boolean) {
        this.Set('CacheHit', value);
    }

    /**
    * * Field Name: ResponseCode
    * * Display Name: Response Code
    * * SQL Data Type: int
    * * Description: HTTP response code returned to client
    */
    get ResponseCode(): number {
        return this.Get('ResponseCode');
    }
    set ResponseCode(value: number) {
        this.Set('ResponseCode', value);
    }

    /**
    * * Field Name: ExecutionContext
    * * Display Name: Execution Context
    * * SQL Data Type: nvarchar(500)
    * * Description: Context where component is being executed
    */
    get ExecutionContext(): string | null {
        return this.Get('ExecutionContext');
    }
    set ExecutionContext(value: string | null) {
        this.Set('ExecutionContext', value);
    }

    /**
    * * Field Name: ParentComponentID
    * * Display Name: Parent Component ID
    * * SQL Data Type: uniqueidentifier
    * * Description: Parent component if this is nested
    */
    get ParentComponentID(): string | null {
        return this.Get('ParentComponentID');
    }
    set ParentComponentID(value: string | null) {
        this.Set('ParentComponentID', value);
    }

    /**
    * * Field Name: SessionID
    * * Display Name: Session ID
    * * SQL Data Type: nvarchar(255)
    * * Description: Session identifier for usage pattern analysis
    */
    get SessionID(): string | null {
        return this.Get('SessionID');
    }
    set SessionID(value: string | null) {
        this.Set('SessionID', value);
    }

    /**
    * * Field Name: UserAgent
    * * Display Name: User Agent
    * * SQL Data Type: nvarchar(1000)
    * * Description: Client user agent string
    */
    get UserAgent(): string | null {
        return this.Get('UserAgent');
    }
    set UserAgent(value: string | null) {
        this.Set('UserAgent', value);
    }

    /**
    * * Field Name: IPAddress
    * * Display Name: IP Address
    * * SQL Data Type: nvarchar(45)
    * * Description: Client IP address for geographic analytics
    */
    get IPAddress(): string | null {
        return this.Get('IPAddress');
    }
    set IPAddress(value: string | null) {
        this.Set('IPAddress', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person
    * * SQL Data Type: nvarchar(201)
    */
    get Person(): string | null {
        return this.Get('Person');
    }

    /**
    * * Field Name: APIKey
    * * Display Name: API Key
    * * SQL Data Type: nvarchar(255)
    */
    get APIKey(): string | null {
        return this.Get('APIKey');
    }
}


/**
 * User Component Versions - strongly typed entity sub-class
 * * Schema: Skip
 * * Base Table: UserComponentVersion
 * * Base View: vwUserComponentVersions
 * * @description Version history for user-generated components with full specifications
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'User Component Versions')
export class SkipUserComponentVersionEntity extends BaseEntity<SkipUserComponentVersionEntityType> {
    /**
    * Loads the User Component Versions record from the database
    * @param ID: string - primary key value to load the User Component Versions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof SkipUserComponentVersionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ComponentID
    * * Display Name: Component ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: User Components (vwUserComponents.ID)
    */
    get ComponentID(): string {
        return this.Get('ComponentID');
    }
    set ComponentID(value: string) {
        this.Set('ComponentID', value);
    }

    /**
    * * Field Name: Version
    * * Display Name: Version
    * * SQL Data Type: nvarchar(50)
    * * Description: Semantic version number (e.g., 1.0.0)
    */
    get Version(): string {
        return this.Get('Version');
    }
    set Version(value: string) {
        this.Set('Version', value);
    }

    /**
    * * Field Name: Specification
    * * Display Name: Specification
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Full component JSON specification including code and metadata
    */
    get Specification(): string {
        return this.Get('Specification');
    }
    set Specification(value: string) {
        this.Set('Specification', value);
    }

    /**
    * * Field Name: SpecificationHash
    * * Display Name: Specification Hash
    * * SQL Data Type: nvarchar(64)
    * * Description: SHA-256 hash of specification for cache validation
    */
    get SpecificationHash(): string {
        return this.Get('SpecificationHash');
    }
    set SpecificationHash(value: string) {
        this.Set('SpecificationHash', value);
    }

    /**
    * * Field Name: UserRequest
    * * Display Name: User Request
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Original user prompt or request that triggered generation
    */
    get UserRequest(): string | null {
        return this.Get('UserRequest');
    }
    set UserRequest(value: string | null) {
        this.Set('UserRequest', value);
    }

    /**
    * * Field Name: GenerationContext
    * * Display Name: Generation Context
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON context including model used, parameters, and generation settings
    */
    get GenerationContext(): string | null {
        return this.Get('GenerationContext');
    }
    set GenerationContext(value: string | null) {
        this.Set('GenerationContext', value);
    }

    /**
    * * Field Name: GenerationType
    * * Display Name: Generation Type
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Initial
    * * Value List Type: List
    * * Possible Values 
    *   * Initial
    *   * Revision
    * * Description: Type of generation: Initial or Revision
    */
    get GenerationType(): 'Initial' | 'Revision' {
        return this.Get('GenerationType');
    }
    set GenerationType(value: 'Initial' | 'Revision') {
        this.Set('GenerationType', value);
    }

    /**
    * * Field Name: RevisionRequest
    * * Display Name: Revision Request
    * * SQL Data Type: nvarchar(MAX)
    * * Description: User revision request if this is a revision
    */
    get RevisionRequest(): string | null {
        return this.Get('RevisionRequest');
    }
    set RevisionRequest(value: string | null) {
        this.Set('RevisionRequest', value);
    }

    /**
    * * Field Name: RevisionSentiment
    * * Display Name: Revision Sentiment
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON sentiment analysis of revision request
    */
    get RevisionSentiment(): string | null {
        return this.Get('RevisionSentiment');
    }
    set RevisionSentiment(value: string | null) {
        this.Set('RevisionSentiment', value);
    }

    /**
    * * Field Name: UserSentimentScore
    * * Display Name: User Sentiment Score
    * * SQL Data Type: int
    * * Description: Inferred user sentiment score from 0-100 based on feedback language
    */
    get UserSentimentScore(): number | null {
        return this.Get('UserSentimentScore');
    }
    set UserSentimentScore(value: number | null) {
        this.Set('UserSentimentScore', value);
    }

    /**
    * * Field Name: HasCustomProps
    * * Display Name: Has Custom Props
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether component accepts custom props beyond standard ones
    */
    get HasCustomProps(): boolean | null {
        return this.Get('HasCustomProps');
    }
    set HasCustomProps(value: boolean | null) {
        this.Set('HasCustomProps', value);
    }

    /**
    * * Field Name: HasRequiredCustomProps
    * * Display Name: Has Required Custom Props
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether component has required custom props
    */
    get HasRequiredCustomProps(): boolean | null {
        return this.Get('HasRequiredCustomProps');
    }
    set HasRequiredCustomProps(value: boolean | null) {
        this.Set('HasRequiredCustomProps', value);
    }

    /**
    * * Field Name: HasCustomEvents
    * * Display Name: Has Custom Events
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether component emits custom events
    */
    get HasCustomEvents(): boolean | null {
        return this.Get('HasCustomEvents');
    }
    set HasCustomEvents(value: boolean | null) {
        this.Set('HasCustomEvents', value);
    }

    /**
    * * Field Name: RequiresData
    * * Display Name: Requires Data
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether component requires data from parent or loads its own
    */
    get RequiresData(): boolean | null {
        return this.Get('RequiresData');
    }
    set RequiresData(value: boolean | null) {
        this.Set('RequiresData', value);
    }

    /**
    * * Field Name: DependencyCount
    * * Display Name: Dependency Count
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Number of child components this component depends on
    */
    get DependencyCount(): number | null {
        return this.Get('DependencyCount');
    }
    set DependencyCount(value: number | null) {
        this.Set('DependencyCount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Component
    * * Display Name: Component
    * * SQL Data Type: nvarchar(250)
    */
    get Component(): string {
        return this.Get('Component');
    }

    /**
    * * Field Name: ComponentNamespace
    * * Display Name: Component Namespace
    * * SQL Data Type: nvarchar(599)
    */
    get ComponentNamespace(): string {
        return this.Get('ComponentNamespace');
    }

    /**
    * * Field Name: ComponentOrganizationID
    * * Display Name: Component Organization ID
    * * SQL Data Type: uniqueidentifier
    */
    get ComponentOrganizationID(): string {
        return this.Get('ComponentOrganizationID');
    }
}


/**
 * User Components - strongly typed entity sub-class
 * * Schema: Skip
 * * Base Table: UserComponent
 * * Base View: vwUserComponents
 * * @description Main catalog of user-generated components for Skip multi-tenant SaaS
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'User Components')
export class SkipUserComponentEntity extends BaseEntity<SkipUserComponentEntityType> {
    /**
    * Loads the User Components record from the database
    * @param ID: string - primary key value to load the User Components record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof SkipUserComponentEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    */
    get PersonID(): string {
        return this.Get('PersonID');
    }
    set PersonID(value: string) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: Namespace
    * * Display Name: Namespace
    * * SQL Data Type: nvarchar(599)
    * * Description: Hierarchical namespace path following format @skip/org-{orgId}/contact-{contactId}
    */
    get Namespace(): string {
        return this.Get('Namespace');
    }
    set Namespace(value: string) {
        this.Set('Namespace', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(250)
    * * Description: Component name within the namespace, must be unique within namespace
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(1000)
    * * Description: User-friendly display title for UI presentation
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Detailed description of component functionality and purpose
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: Type
    * * Display Name: Type
    * * SQL Data Type: nvarchar(255)
    * * Description: Component type classification: Report, Dashboard, Form, Table, Chart, Navigation, Search, Widget, Utility, or Other
    */
    get Type(): string | null {
        return this.Get('Type');
    }
    set Type(value: string | null) {
        this.Set('Type', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person
    * * SQL Data Type: nvarchar(201)
    */
    get Person(): string {
        return this.Get('Person');
    }
}
